import { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Input,
  Modal,
  Radio,
  Space,
  Switch,
  Table,
  Typography,
} from '@arco-design/web-react'
import { IconDelete, IconPlus, IconSave, IconSearch } from '@arco-design/web-react/icon'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { Pill } from '@/utils/render'
import type { OptionsAPI } from '../useOptions'
import {
  PAGE_SIZE,
  PRICE_SUFFIX,
  buildSummaryText,
  getModelStatusLabels,
  hasValue,
  useModelPricingEditorState,
  type FilterMode,
  type ModelPricingState,
} from './useModelPricingEditorState'

interface PriceInputProps {
  label: string
  value: string
  placeholder: string
  onChange: (value: string) => void
  suffix?: string
  disabled?: boolean
  extraText?: string
  headerAction?: React.ReactNode
  hidden?: boolean
  switchKey?: string
}

function PriceInput({
  label,
  value,
  placeholder,
  onChange,
  suffix = PRICE_SUFFIX,
  disabled = false,
  extraText = '',
  headerAction = null,
  hidden = false,
  switchKey,
}: PriceInputProps) {
  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center justify-between gap-3 font-medium text-slate-700">
        <span>{label}</span>
        <span key={switchKey}>{headerAction}</span>
      </div>
      {!hidden ? (
        <Input value={value} placeholder={placeholder} onChange={onChange} suffix={suffix} disabled={disabled} />
      ) : null}
      {extraText ? <div className="mt-1 text-xs text-slate-500">{extraText}</div> : null}
    </div>
  )
}

export interface VisualModelPricingEditorProps {
  options: OptionsAPI
  candidateModelNames?: string[]
  filterMode?: FilterMode
  allowAddModel?: boolean
  allowDeleteModel?: boolean
  showConflictFilter?: boolean
  listDescription?: string
  emptyTitle?: string
  emptyDescription?: string
}

const NO_CANDIDATE_MODELS: string[] = []

export default function VisualModelPricingEditor({
  options,
  candidateModelNames = NO_CANDIDATE_MODELS,
  filterMode = 'all',
  allowAddModel = true,
  allowDeleteModel = true,
  showConflictFilter = true,
  listDescription = '',
  emptyTitle = '',
  emptyDescription = '',
}: VisualModelPricingEditorProps) {
  const { isMobile } = useBreakpoint()
  const [addVisible, setAddVisible] = useState(false)
  const [batchVisible, setBatchVisible] = useState(false)
  const [newModelName, setNewModelName] = useState('')

  const {
    selectedModel,
    selectedModelName,
    selectedModelNames,
    setSelectedModelName,
    setSelectedModelNames,
    searchText,
    setSearchText,
    currentPage,
    setCurrentPage,
    loading,
    parseErrors,
    hasParseErrors,
    canSave,
    conflictOnly,
    setConflictOnly,
    filteredModels,
    pagedData,
    selectedWarnings,
    previewRows,
    isOptionalFieldEnabled,
    handleOptionalFieldToggle,
    handleNumericFieldChange,
    handleBillingModeChange,
    handleSubmit,
    addModel,
    deleteModel,
    applySelectedModelPricing,
  } = useModelPricingEditorState({
    options,
    candidateModelNames,
    filterMode,
  })

  const columns = useMemo(
    () => [
      {
        title: '模型名称',
        dataIndex: 'name',
        fixed: 'left' as const,
        width: 200,
        render: (_: unknown, record: ModelPricingState) => (
          <Space>
            <Button
              type="text"
              onClick={() => setSelectedModelName(record.name)}
              style={{ color: record.name === selectedModelName ? 'rgb(var(--primary-6))' : undefined, padding: 0 }}
            >
              {record.name}
            </Button>
            {selectedModelNames.includes(record.name) ? (
              <Pill variant="success">已勾选</Pill>
            ) : null}
          </Space>
        ),
      },
      {
        title: '计费方式',
        dataIndex: 'billingMode',
        width: 120,
        render: (_: unknown, record: ModelPricingState) => (
          <Pill variant={record.billingMode === 'per-request' ? 'info' : 'violet'}>
            {record.billingMode === 'per-request' ? '按次计费' : '按量计费'}
          </Pill>
        ),
      },
      {
        title: '价格摘要',
        width: 320,
        render: (_: unknown, record: ModelPricingState) => (
          <span className="text-sm whitespace-pre-wrap">{buildSummaryText(record)}</span>
        ),
      },
      {
        title: '状态',
        width: 200,
        render: (_: unknown, record: ModelPricingState) => (
          <Space wrap size="mini">
            {getModelStatusLabels(record).map((label) => (
              <Pill key={label.text} variant={label.variant}>
                {label.text}
              </Pill>
            ))}
          </Space>
        ),
      },
      {
        title: '操作',
        width: 80,
        fixed: 'right' as const,
        render: (_: unknown, record: ModelPricingState) =>
          allowDeleteModel ? (
            <Button type="text" status="danger" icon={<IconDelete />} onClick={() => deleteModel(record.name)} />
          ) : null,
      },
    ],
    [allowDeleteModel, deleteModel, selectedModelName, selectedModelNames, setSelectedModelName]
  )

  const handleAddModel = () => {
    if (addModel(newModelName)) {
      setNewModelName('')
      setAddVisible(false)
    }
  }

  return (
    <>
      <Space direction="vertical" size="medium" style={{ width: '100%' }}>
        {hasParseErrors ? (
          <Alert
            type="error"
            content={
              <div>
                <div className="font-medium mb-1">以下 option JSON 解析失败，无法保存价格设置</div>
                {Object.entries(parseErrors).map(([key, error]) => (
                  <div key={key} className="text-sm">
                    {key}：{error}
                  </div>
                ))}
                <div className="text-xs mt-2 text-slate-500">
                  请前往「模型倍率设置」高级 Tab 修复原始 JSON 后再保存。
                </div>
              </div>
            }
          />
        ) : null}
        <Space wrap className="settings-toolbar-compact">
          {allowAddModel ? (
            <Button icon={<IconPlus />} onClick={() => setAddVisible(true)} long={isMobile}>
              添加模型
            </Button>
          ) : null}
          <Button
            type="primary"
            icon={<IconSave />}
            loading={loading}
            disabled={!canSave}
            onClick={handleSubmit}
            long={isMobile}
          >
            保存价格设置
          </Button>
          <Button
            disabled={!selectedModel || selectedModelNames.length === 0}
            onClick={() => setBatchVisible(true)}
            long={isMobile}
          >
            批量应用当前模型价格
            {selectedModelNames.length > 0 ? ` (${selectedModelNames.length})` : ''}
          </Button>
          <Input
            prefix={<IconSearch />}
            placeholder="搜索模型名称"
            value={searchText}
            onChange={setSearchText}
            style={{ width: isMobile ? '100%' : 220 }}
            allowClear
          />
          {showConflictFilter ? (
            <Checkbox checked={conflictOnly} onChange={setConflictOnly}>
              只看冲突
            </Checkbox>
          ) : null}
        </Space>

        {listDescription ? <div className="text-sm text-slate-500">{listDescription}</div> : null}
        {selectedModelNames.length > 0 ? (
          <div className="settings-info-strip">已勾选 {selectedModelNames.length} 个模型</div>
        ) : null}

        <div
          className="ratio-pricing-grid grid gap-3"
          style={{
            gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(360px, 1.05fr) minmax(420px, 1fr)',
          }}
        >
          <div className="settings-section ratio-model-list-panel" style={{ order: isMobile ? 2 : 0 }}>
            <Table
              columns={columns}
              data={pagedData}
              rowKey="name"
              rowSelection={{
                selectedRowKeys: selectedModelNames,
                onChange: (keys) => setSelectedModelNames(keys as string[]),
              }}
              pagination={{
                current: currentPage,
                pageSize: PAGE_SIZE,
                total: filteredModels.length,
                onChange: setCurrentPage,
                showTotal: true,
              }}
              onRow={(record) => ({
                onClick: () => setSelectedModelName(record.name),
                style: {
                  background: selectedModelNames.includes(record.name)
                    ? 'var(--color-success-light-1)'
                    : record.name === selectedModelName
                      ? 'var(--color-primary-light-1)'
                      : undefined,
                  cursor: 'pointer',
                },
              })}
              scroll={{ x: 900, y: isMobile ? undefined : 'calc(100vh - 260px)' }}
              noDataElement={
                <Empty
                  description={emptyTitle || '暂无模型'}
                  style={{ padding: '24px 0' }}
                />
              }
            />
          </div>

          <div className="settings-section ratio-model-editor-panel" style={{ order: isMobile ? 1 : 0 }}>
            <div className="ratio-editor-heading">
              <div className="ratio-editor-title">
                {selectedModel ? selectedModel.name : '模型计费编辑器'}
              </div>
              {selectedModel ? (
                <Pill variant={selectedModel.billingMode === 'per-request' ? 'info' : 'violet'}>
                  {selectedModel.billingMode === 'per-request' ? '按次计费' : '按量计费'}
                </Pill>
              ) : null}
            </div>
            {!selectedModel ? (
              <Empty
                description={
                  emptyDescription || emptyTitle || '请先新增模型或从左侧列表选择一个模型'
                }
                style={{ padding: '32px 0' }}
              />
            ) : (
              <div>
                <div className="mb-4">
                  <div className="mb-2 font-medium text-slate-700">计费方式</div>
                  <Radio.Group
                    type="button"
                    value={selectedModel.billingMode}
                    onChange={handleBillingModeChange}
                  >
                    <Radio value="per-token">按量计费</Radio>
                    <Radio value="per-request">按次计费</Radio>
                  </Radio.Group>
                  <div className="mt-2 text-xs text-slate-500">
                    这个界面默认按价格填写，保存时会自动换算回后端需要的倍率 JSON。
                  </div>
                </div>

                {selectedWarnings.length > 0 ? (
                  <Alert
                    type="warning"
                    className="!mb-4"
                    content={
                      <div>
                        <div className="font-medium mb-1">当前提示</div>
                        {selectedWarnings.map((warning) => (
                          <div key={warning} className="text-sm">
                            {warning}
                          </div>
                        ))}
                      </div>
                    }
                  />
                ) : null}

                {selectedModel.billingMode === 'per-request' ? (
                  <PriceInput
                    label="固定价格"
                    value={selectedModel.fixedPrice}
                    placeholder="输入每次调用价格"
                    suffix="$/次"
                    onChange={(value) => handleNumericFieldChange('fixedPrice', value)}
                    extraText="适合 MJ / 任务类等按次收费模型。"
                  />
                ) : (
                  <>
                    <div className="settings-subsection mb-3">
                      <div className="settings-section-title mb-3">基础价格</div>
                      <PriceInput
                        label="输入价格"
                        value={selectedModel.inputPrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('inputPrice', value)}
                      />
                      {selectedModel.completionRatioLocked ? (
                        <Alert
                          type="warning"
                          className="!mb-3"
                          content={`该模型补全倍率由后端固定为 ${selectedModel.lockedCompletionRatio || '-'}。补全价格不能在这里修改。`}
                        />
                      ) : null}
                      <PriceInput
                        label="补全价格"
                        value={selectedModel.completionPrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('completionPrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-completionPrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'completionPrice')}
                            disabled={selectedModel.completionRatioLocked}
                            onChange={(checked) => handleOptionalFieldToggle('completionPrice', checked)}
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'completionPrice')}
                        disabled={!hasValue(selectedModel.inputPrice) || selectedModel.completionRatioLocked}
                        extraText={
                          selectedModel.completionRatioLocked
                            ? `后端固定倍率：${selectedModel.lockedCompletionRatio || '-'}。该字段仅展示换算后的价格。`
                            : !isOptionalFieldEnabled(selectedModel, 'completionPrice')
                              ? '当前未启用，需要时再打开即可。'
                              : ''
                        }
                      />
                      <PriceInput
                        label="缓存读取价格"
                        value={selectedModel.cachePrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('cachePrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-cachePrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'cachePrice')}
                            onChange={(checked) => handleOptionalFieldToggle('cachePrice', checked)}
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'cachePrice')}
                        disabled={!hasValue(selectedModel.inputPrice)}
                        extraText={
                          !isOptionalFieldEnabled(selectedModel, 'cachePrice')
                            ? '当前未启用，需要时再打开即可。'
                            : ''
                        }
                      />
                      <PriceInput
                        label="缓存创建价格"
                        value={selectedModel.createCachePrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('createCachePrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-createCachePrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'createCachePrice')}
                            onChange={(checked) => handleOptionalFieldToggle('createCachePrice', checked)}
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'createCachePrice')}
                        disabled={!hasValue(selectedModel.inputPrice)}
                        extraText={
                          !isOptionalFieldEnabled(selectedModel, 'createCachePrice')
                            ? '当前未启用，需要时再打开即可。'
                            : ''
                        }
                      />
                    </div>

                    <div className="settings-subsection mb-3">
                      <div className="mb-2">
                        <div className="settings-section-title">扩展价格</div>
                        <div className="settings-section-desc">这些价格都是可选项，不填也可以。</div>
                      </div>
                      <PriceInput
                        label="图片输入价格"
                        value={selectedModel.imagePrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('imagePrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-imagePrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'imagePrice')}
                            onChange={(checked) => handleOptionalFieldToggle('imagePrice', checked)}
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'imagePrice')}
                        disabled={!hasValue(selectedModel.inputPrice)}
                      />
                      <PriceInput
                        label="音频输入价格"
                        value={selectedModel.audioInputPrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('audioInputPrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-audioInputPrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'audioInputPrice')}
                            onChange={(checked) => handleOptionalFieldToggle('audioInputPrice', checked)}
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'audioInputPrice')}
                        disabled={!hasValue(selectedModel.inputPrice)}
                      />
                      <PriceInput
                        label="音频补全价格"
                        value={selectedModel.audioOutputPrice}
                        placeholder="输入 $/1M tokens"
                        onChange={(value) => handleNumericFieldChange('audioOutputPrice', value)}
                        switchKey={`${selectedModel?.name ?? 'none'}-audioOutputPrice`}
                        headerAction={
                          <Switch
                            size="small"
                            checked={isOptionalFieldEnabled(selectedModel, 'audioOutputPrice')}
                            disabled={!isOptionalFieldEnabled(selectedModel, 'audioInputPrice')}
                            onChange={(checked) => handleOptionalFieldToggle('audioOutputPrice', checked)}
                          />
                        }
                        hidden={!isOptionalFieldEnabled(selectedModel, 'audioOutputPrice')}
                        disabled={!hasValue(selectedModel.audioInputPrice)}
                        extraText={
                          !isOptionalFieldEnabled(selectedModel, 'audioInputPrice')
                            ? '请先开启并填写音频输入价格。'
                            : !isOptionalFieldEnabled(selectedModel, 'audioOutputPrice')
                              ? '当前未启用，需要时再打开即可。'
                              : ''
                        }
                      />
                    </div>
                  </>
                )}

                <div className="settings-subsection">
                  <div className="settings-section-title mb-2">保存预览</div>
                  <div className="settings-section-desc mb-3">
                    下面展示该模型保存后会写入的后端 key 与 value。
                  </div>
                  <div className="divide-y divide-slate-100 text-sm">
                    {previewRows.map((row) => (
                      <div key={row.key} className="grid gap-2 py-2" style={{ gridTemplateColumns: 'minmax(140px, 180px) 1fr' }}>
                        <Typography.Text bold className="!text-xs">
                          {row.label}
                        </Typography.Text>
                        <Typography.Text className="!text-xs font-mono">{row.value}</Typography.Text>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Space>

      {allowAddModel ? (
        <Modal
          title="添加模型"
          visible={addVisible}
          onCancel={() => {
            setAddVisible(false)
            setNewModelName('')
          }}
          onOk={handleAddModel}
        >
          <Input
            value={newModelName}
            placeholder="输入模型名称，例如 gpt-4.1"
            onChange={setNewModelName}
          />
        </Modal>
      ) : null}

      <Modal
        title="批量应用当前模型价格"
        visible={batchVisible}
        onCancel={() => setBatchVisible(false)}
        onOk={() => {
          if (applySelectedModelPricing()) setBatchVisible(false)
        }}
      >
        <div className="space-y-2 text-sm text-slate-600">
          {selectedModel ? (
            <>
              <div>
                将把当前编辑中的模型 <strong>{selectedModel.name}</strong> 的价格配置，批量应用到已勾选的{' '}
                {selectedModelNames.length} 个模型。
              </div>
              <div className="text-amber-700">
                此操作会覆盖已勾选模型现有的定价配置，包括固定价与全部按量倍率键。请确认后再继续。
              </div>
            </>
          ) : (
            '请先选择一个作为模板的模型'
          )}
        </div>
      </Modal>
    </>
  )
}
